import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState(0);
  const [statsAnimated, setStatsAnimated] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('fade-in--visible');
      });
    }, { threshold: 0.08, rootMargin: '0px 0px 80px 0px' });

    document.querySelectorAll('.fade-in').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const statsObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !statsAnimated) {
        setStatsAnimated(true);
        let current = 0;
        const timer = setInterval(() => {
          current += 2;
          if (current >= 100) { current = 100; clearInterval(timer); }
          setEmpresas(current);
        }, 40);
      }
    }, { threshold: 0.5 });

    if (statsRef.current) statsObserver.observe(statsRef.current);
    return () => statsObserver.disconnect();
  }, [statsAnimated]);

  const nav = (path) => {
    navigate(path);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  const services = [
    { icon: '⚙️', title: 'Sistemas de Gestión', desc: 'Implementación de mejores prácticas internacionales para optimizar la eficiencia organizacional.' },
    { icon: '🏆', title: 'Asesoría en Certificaciones', desc: 'Acompañamiento experto para obtener certificaciones nacionales e internacionales exitosamente.' },
    { icon: '🎓', title: 'Capacitación Especializada', desc: 'Programas de entrenamiento basados en transferencia de conocimiento con expertos de industria.' },
    { icon: '📈', title: 'Gestión Estratégica de TI', desc: 'Modelos de gestión orientados a generar ventaja competitiva real a través de la tecnología.' },
  ];

  const sectors = [
    { name: 'Gobierno', icon: '🏛️', desc: 'Gobernanza de TI, transparencia y marcos regulatorios para organismos públicos.' },
    { name: 'Automotriz', icon: '🚗', desc: 'IATF 16949, gestión de proveedores y calidad en procesos de manufactura.' },
    { name: 'Financiero', icon: '🏦', desc: 'Controles internos, gestión de riesgos y cumplimiento normativo con COBIT.' },
    { name: 'Petróleo', icon: '⚡', desc: 'Seguridad operacional, normas ambientales y SSPA para el sector energético.' },
  ];

  const clients = [
    'Harvest',
    'Plexus',
  ];

  const steps = [
    { number: '01', title: 'Diagnóstico', desc: 'Evaluamos el estado actual de tu organización e identificamos brechas frente a la norma objetivo.' },
    { number: '02', title: 'Plan de Acción', desc: 'Diseñamos una hoja de ruta personalizada con tiempos, responsables y entregables claros.' },
    { number: '03', title: 'Implementación', desc: 'Acompañamos a tu equipo en cada etapa, desde la documentación hasta la operación.' },
    { number: '04', title: 'Certificación', desc: 'Preparamos tu organización para la auditoría y te apoyamos hasta obtener el certificado.' },
  ];

  const reasons = [
    { icon: '🎯', title: 'Enfoque en resultados', desc: 'No solo asesoramos: nos comprometemos con los resultados y el éxito de tu certificación.' },
    { icon: '🔬', title: 'Metodología comprobada', desc: 'Procesos estructurados basados en 20 años de experiencia y mejores prácticas internacionales.' },
    { icon: '👨‍💼', title: 'Equipo certificado', desc: 'Consultores con certificaciones internacionales vigentes en cada área de especialidad.' },
    { icon: '🤝', title: 'Acompañamiento total', desc: 'Estamos contigo desde el diagnóstico hasta después de la certificación, sin dejarte solo.' },
  ];

  const testimonials = [
    {
      quote: 'La consultoría de ISQS transformó nuestra gestión de TI por completo. Logramos la certificación ISO/IEC 20000 en tiempo récord y con resultados reales.',
      name: 'Carlos Mendoza',
      role: 'Director de TI',
      company: 'Sector Financiero',
      initial: 'C',
    },
    {
      quote: 'Metodología clara, equipo muy profesional y comunicación constante. Nos guiaron en cada paso sin complicarnos. Los recomiendo ampliamente.',
      name: 'Ana García',
      role: 'Gerente de Calidad',
      company: 'Sector Automotriz',
      initial: 'A',
    },
    {
      quote: 'Conocimiento técnico y compromiso real. Superamos las expectativas del organismo evaluador gracias al trabajo de ISQS Services.',
      name: 'Roberto Sánchez',
      role: 'Director General',
      company: 'Organismo de Gobierno',
      initial: 'R',
    },
  ];

  return (
    <main className="home">

      {/* Hero */}
      <section className="hero">
        <img
          className="hero__video"
          src="/fondo 2.jpg"
          alt="Hero background"
        />
        <div className="hero__overlay" />
        <div className="hero__container">
          <div className="hero__content fade-in">
            <h1 className="hero__title">
              Consultoría Profesional<br />
              <span className="hero__title-accent">para Empresas que Crecen</span>
            </h1>
            <p className="hero__subtitle">
              Implementamos sistemas de gestión, certificaciones y estrategias de TI
              que transforman organizaciones en sectores clave de México.
            </p>
            <div className="hero__actions">
              <button className="btn btn--primary" onClick={() => nav('/servicios')}>
                Ver Servicios
              </button>
              <button className="btn btn--outline" onClick={() => nav('/contacto')}>
                Hablar con un Experto
              </button>
            </div>
            <div className="hero__trust">
              <div className="hero__trust-item"><span className="hero__trust-dot" />ISO 9001</div>
              <div className="hero__trust-item"><span className="hero__trust-dot" />ISO/IEC 27001</div>
              <div className="hero__trust-item"><span className="hero__trust-dot" />COBIT · ITIL</div>
              <div className="hero__trust-item"><span className="hero__trust-dot" />IATF 16949</div>
            </div>
          </div>


        </div>
      </section>

      {/* Stats bar */}
      <section className="stats" ref={statsRef}>
        <div className="container">
          <div className="stats__grid">
            <div className="stat-item fade-in">
              <span className="stat-item__number">{empresas}+</span>
              <span className="stat-item__label">Empresas atendidas</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item fade-in">
              <span className="stat-item__number">20</span>
              <span className="stat-item__label">Años de experiencia</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item fade-in">
              <span className="stat-item__number">130+</span>
              <span className="stat-item__label">Proyectos completados</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item fade-in">
              <span className="stat-item__number">4</span>
              <span className="stat-item__label">Sectores especializados</span>
            </div>
          </div>
        </div>
      </section>

      {/* Client logos */}
      <section className="clients">
        <div className="container">
          <p className="clients__label fade-in">Empresas que confían en nosotros</p>
          <div className="clients__grid fade-in">
            {clients.map(c => (
              <div key={c} className="client-logo">
                <span className="client-logo__dot" />
                <span>{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="services-section">
        <div className="container">
          <div className="section-header fade-in">
            <span className="section-label">Lo que hacemos</span>
            <h2 className="section-title">Nuestros Servicios</h2>
            <p className="section-subtitle">
              Soluciones de consultoría respaldadas por 20 años de experiencia en normas nacionales e internacionales.
            </p>
          </div>
          <div className="services-grid">
            {services.map(s => (
              <div key={s.title} className="service-card fade-in">
                <div className="service-card__icon-wrap">{s.icon}</div>
                <h3 className="service-card__title">{s.title}</h3>
                <p className="service-card__desc">{s.desc}</p>
                <button className="service-card__link" onClick={() => nav('/servicios')}>
                  Conocer más →
                </button>
              </div>
            ))}
          </div>
          <div className="services-section__cta fade-in">
            <button className="btn btn--outline" onClick={() => nav('/servicios')}>
              Ver todos los servicios
            </button>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="process-section">
        <div className="container">
          <div className="section-header fade-in">
            <span className="section-label">Nuestra metodología</span>
            <h2 className="section-title">¿Cómo trabajamos?</h2>
            <p className="section-subtitle">
              Un proceso claro y estructurado que garantiza resultados medibles en cada etapa.
            </p>
          </div>
          <div className="process-grid">
            {steps.map(step => (
              <div key={step.number} className="process-step fade-in">
                <div className="process-step__number">{step.number}</div>
                <h3 className="process-step__title">{step.title}</h3>
                <p className="process-step__desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="why-section">
        <div className="container">
          <div className="section-header fade-in">
            <span className="section-label">Nuestra diferencia</span>
            <h2 className="section-title">¿Por qué elegirnos?</h2>
            <p className="section-subtitle">
              No somos solo consultores. Somos el equipo que te acompaña hasta alcanzar tus objetivos.
            </p>
          </div>
          <div className="why-grid">
            {reasons.map(r => (
              <div key={r.title} className="why-card fade-in">
                <div className="why-card__icon">{r.icon}</div>
                <h3 className="why-card__title">{r.title}</h3>
                <p className="why-card__desc">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="sectors">
        <div className="container">
          <div className="section-header fade-in">
            <span className="section-label">Industrias</span>
            <h2 className="section-title">Sectores que atendemos</h2>
            <p className="section-subtitle">
              Experiencia especializada en los sectores más exigentes del mercado mexicano.
            </p>
          </div>
          <div className="sectors__grid">
            {sectors.map(s => (
              <div key={s.name} className="sector-card fade-in">
                <div className="sector-card__icon">{s.icon}</div>
                <h3 className="sector-card__name">{s.name}</h3>
                <p className="sector-card__desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section className="about-section">
        <div className="container">
          <div className="about-grid">
            <div className="about-content fade-in">
              <span className="section-label">Quiénes somos</span>
              <h2 className="about-title">Expertos en Gestión y Tecnología desde 2004</h2>
              <p className="about-text">
                Somos una empresa de Servicios y Consultoría Profesional con sede en la Ciudad de México.
                Nos especializamos en la implementación de Sistemas de Gestión basados en Normas Nacionales
                e Internacionales para organizaciones de sectores estratégicos.
              </p>
              <p className="about-text">
                Nuestro equipo asesora a empresas de Gobierno, Automotriz, Financiero y Petróleo,
                ayudándolas a alcanzar certificaciones y ventajas competitivas reales a través de la tecnología.
              </p>
              <button className="btn btn--primary" onClick={() => nav('/nosotros')}>
                Conocer más sobre nosotros
              </button>
            </div>
            <div className="about-metrics fade-in">
              <div className="metric-card">
                <span className="metric-card__icon">🏅</span>
                <div>
                  <p className="metric-card__title">Certificaciones exitosas</p>
                  <p className="metric-card__desc">Proyectos llave en mano individuales e integrados</p>
                </div>
              </div>
              <div className="metric-card">
                <span className="metric-card__icon">🌐</span>
                <div>
                  <p className="metric-card__title">Normas internacionales</p>
                  <p className="metric-card__desc">ISO, ITIL, COBIT y estándares de industria</p>
                </div>
              </div>
              <div className="metric-card">
                <span className="metric-card__icon">👥</span>
                <div>
                  <p className="metric-card__title">Personal calificado</p>
                  <p className="metric-card__desc">Expertos con años de experiencia en cada industria</p>
                </div>
              </div>
              <div className="metric-card">
                <span className="metric-card__icon">📋</span>
                <div>
                  <p className="metric-card__title">Metodología probada</p>
                  <p className="metric-card__desc">Procesos estructurados con resultados medibles</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header fade-in">
            <span className="section-label">Lo que dicen nuestros clientes</span>
            <h2 className="section-title">Testimonios</h2>
          </div>
          <div className="testimonials-grid">
            {testimonials.map(t => (
              <div key={t.name} className="testimonial-card fade-in">
                <div className="testimonial-card__quote">"</div>
                <p className="testimonial-card__text">{t.quote}</p>
                <div className="testimonial-card__author">
                  <div className="testimonial-card__avatar">{t.initial}</div>
                  <div>
                    <p className="testimonial-card__name">{t.name}</p>
                    <p className="testimonial-card__role">{t.role} · {t.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content fade-in">
            <h2 className="cta-title">¿Listo para transformar tu organización?</h2>
            <p className="cta-subtitle">
              Hablemos sobre cómo podemos ayudarte a alcanzar tus objetivos de gestión y certificación.
            </p>
            <div className="cta-actions">
              <button className="btn btn--primary btn--large" onClick={() => nav('/contacto')}>
                Solicitar Consulta Gratuita
              </button>
              <button className="btn btn--ghost-light" onClick={() => nav('/servicios')}>
                Explorar Servicios
              </button>
            </div>
          </div>
        </div>
      </section>

    </main>
  );
};

export default Home;
